<template>
  <v-ons-page class="ja-stream-group-detail">
    <div class="page__background" style="background-color: white" />
    <common-header>
      <template #header>
        <Button
          v-if="!streamingScheduleId"
          modifier="quiet"
          class="header-back-button gray-color"
          @click="() => moveBackButton(router)"
        >
          <v-ons-icon icon="md-chevron-left" />
        </Button>
        <span>配信グループの詳細</span>
        <template v-if="displayState === DISPLAYSTATE.LOADING_DONE">
          <!-- 予約配信のとき -->
          <div v-if="streamingScheduleId" class="header-menu-wrapper">
            <div class="header-menu">
              <button class="menu-button" @click="setIsOpenHeaderMenu(true)">
                <img :src="ICON_DOTS" alt="メニュー" />
              </button>
              <transition-group>
                <template v-if="isOpenHeaderMenu">
                  <div class="header-menu-list" key="header-menu-list">
                    <button class="header-menu-item" @click="moveContentSelect">編集</button>
                    <button class="header-menu-item delete-button" @click="clickDeleteDialog">
                      削除
                    </button>
                  </div>
                  <button
                    @click="setIsOpenHeaderMenu(false)"
                    class="header-menu-list-mask"
                    key="header-menu-list-mask"
                  />
                </template>
              </transition-group>
            </div>
          </div>
          <!-- 予約配信以外 -->
          <trash-button
            v-else
            :onClickFunc="clickDeleteDialog"
            class="header-delete-button gray-color"
          />
        </template>
      </template>
    </common-header>

    <div class="stream-group-detail-area">
      <div class="stream-group-element-area">
        <label>配信コンテンツ</label>
        <p>{{ contentCount }}コンテンツ</p>
      </div>

      <template v-if="displayState === DISPLAYSTATE.LOADING_DONE">
        <div v-if="contentList.length" class="ja-content-list">
          <ja-content-row
            v-for="content in contentList"
            :key="content.contentId"
            :content="content"
            :contentFrame="content.contentFrameDto"
          />
        </div>

        <empty-content v-else :emptyPageNum="EMPTY_PAGE_NUM.STREAM_GROUP" />
      </template>
      <progress-circular v-show="displayState === DISPLAYSTATE.LOADING_PROGRESS" />

      <div class="stream-group-element-area" style="margin-top: 2rem">
        <label>合計時間</label>
        <div class="duration-wrapper d-flex items-center">
          <span class="stream-group-duration">{{ convertSecToms(streamGroupDuration) }}</span>
        </div>
      </div>
      <div class="stream-group-element-area">
        <label>タイトル</label>
        <p>{{ streamGroupName }}</p>
      </div>
      <!-- 配信予約表示領域 -->
      <JaStreamGroupEditSchedule
        v-if="streamingScheduleId"
        :setStreamingStartTime="setStreamingStartTime"
        :setStreamingEndTime="setStreamingEndTime"
        :streamingStartTime="streamingStartTime"
        :streamingEndTime="streamingEndTime"
        :selectedDaysOfWeek="selectedDaysOfWeek"
        :dayOfWeekButtonClick="dayOfWeekButtonClick"
        :isDisabled="streamingScheduleDetail.uploadStatusId === UPLOAD_STATUS_ID_LIST.CANCEL"
      />
    </div>
    <!-- フッター表示領域 -->
    <common-footer class="footer">
      <template #footer>
        <v-ons-row class="footer-button-area">
          <!-- フッター領域(キャンセル・保存) -->
          <template v-if="streamingScheduleId">
            <small-button
              class="footer-back-button"
              title="キャンセル"
              @click="() => moveBackButton(router)"
            />
            <small-button
              class="footer-upload-button"
              title="保存する"
              :disabled="isSaveButtonDisable || !isFormValid || !isActiveDayOfWeekButton"
              @click="streamGroupScheduleUpload"
            />
          </template>
          <!-- フッター領域(編集する) -->
          <small-button
            v-else
            class="footer-button footer-next-button"
            title="編集する"
            :disabled="!contentList.length"
            @click="moveContentSelect"
          />
        </v-ons-row>
      </template>
    </common-footer>
  </v-ons-page>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import CommonHeader from '@/components/common/CommonHeader.vue';
import CommonFooter from '@/components/common/CommonFooter.vue';
import SmallButton from '@/components/common/SmallButton.vue';
import ProgressCircular from '@/components/common/ProgressCircular.vue';
import JaContentRow from '@/components/JaStreamGroup/JaContentRow.vue';
import EmptyContent from '@/components/common/EmptyContent.vue';
import JaStreamGroupEditSchedule from '@/components/JaStreamGroup/JaStreamGroupEditSchedule.vue';
import { putStreamGroupSchedule } from '@/composable/data_push_common';
import { dataFetchStreamGroupDetail } from '@/composable/data_fetch_common';
import {
  disableScroll,
  enableScroll,
  handleAxiosCommonException,
  moveBackButton,
} from '@/composable/common';
import { onsConfirm } from '@/composable/onsen_common';
import { API_BASE_URL } from '@/composable/constant';
import { AXIOS_TIMEOUT, ICON_DOTS, UPLOAD_STATUS_ID_LIST } from '@/composable/constant_common';
import axios from '@/composable/axios';
import TrashButton from '@/components/common/TrashButton.vue';
import { convertSecToms } from '@/composable/material_common';
import Button from '../common/Button.vue';

const { t } = useI18n();

// 表示用コンテンツリスト
const contentList = ref([]);

// 配信グループ詳細情報
const streamGroupDetail = ref([]);

// 配信予約詳細情報
const streamingScheduleDetail = ref([]);

// 配信グループ名
const streamGroupName = ref('');

// 配信予約ID
const streamingScheduleId = ref(0);

// 配信開始時間比較用
const compareStartTime = ref('');

// 配信開始時間
const streamingStartTime = ref('');

// 配信終了時間比較用
const compareEndTime = ref('');

// 配信終了時間
const streamingEndTime = ref('');

// 選択済曜日リスト比較用
const compareSelectedDaysOfWeek = ref([]);

// 選択済曜日リスト
const selectedDaysOfWeek = ref([]);

// 配信グループ再生秒数
const streamGroupDuration = ref(0);

// 配信グループID
const streamGroupId = ref(0);

// 配信グループ内コンテンツ数
const contentCount = ref(0);

// ローディング完了フラグ
const isLoadingDone = ref(false);

// ヘッダメニュー表示要否
const isOpenHeaderMenu = ref(false);

// ローディングステータス
// LOADING_PROGRESS: ロード中
// LOADING_DONE: ロード完了
const DISPLAYSTATE = {
  LOADING_PROGRESS: 'ロード中',
  LOADING_DONE: 'ロード完了',
};

// axios通信処理待機フラグ
const isAxiosWait = ref(false);

// グローバル系
const router = useRouter();
const store = useStore();

// 画面表示状態
const displayState = computed(() => {
  return isLoadingDone.value ? DISPLAYSTATE.LOADING_DONE : DISPLAYSTATE.LOADING_PROGRESS;
});

// 編集保存ボタン活性判定処理
const isSaveButtonDisable = computed(() => {
  return (
    JSON.stringify([...selectedDaysOfWeek.value].sort()) ===
      JSON.stringify([...compareSelectedDaysOfWeek.value].sort()) &&
    compareStartTime.value === streamingStartTime.value &&
    compareEndTime.value === streamingEndTime.value
  );
});

// 選択済曜日リストに引数の曜日が入っているか判定
const isActiveDayOfWeekButton = computed(
  () => (dayOfWeekName) => selectedDaysOfWeek.value.includes(dayOfWeekName)
);

// フォーム入力情報が全て正しいか判定
const isFormValid = computed(() => {
  const [streamingStartTimeHour, streamingStartTimeMinute] = streamingStartTime.value
    .split(':')
    .map(Number);
  const [streamingEndTimeHour, streamingEndTimeMinute] = streamingEndTime.value
    .split(':')
    .map(Number);
  return (
    streamingStartTime.value !== '' &&
    streamingEndTime.value !== '' &&
    selectedDaysOfWeek.value.length &&
    (streamingStartTimeHour < streamingEndTimeHour ||
      (streamingStartTimeHour === streamingEndTimeHour &&
        streamingStartTimeMinute < streamingEndTimeMinute))
  );
});

/**
 * 配信グループ詳細情報取得処理
 */
const getStreamGroupDetail = async () => {
  isLoadingDone.value = false;
  // 配信グループ詳細情報取得
  streamGroupDetail.value = await dataFetchStreamGroupDetail();
  streamingScheduleDetail.value = streamGroupDetail.value.streamingScheduleDetailDto;

  // 表示用配信グループ情報格納処理
  streamGroupName.value = streamGroupDetail.value.streamGroupName;
  streamGroupDuration.value = streamGroupDetail.value.streamGroupDuration;
  contentCount.value = streamGroupDetail.value.contentCount;
  streamGroupId.value = streamGroupDetail.value.streamGroupId;

  if (streamingScheduleDetail.value) {
    streamingScheduleId.value = streamingScheduleDetail.value.streamingScheduleId;
    streamingStartTime.value = `${`0${streamingScheduleDetail.value.streamingStartHour}`.slice(
      -2
    )}:${`0${streamingScheduleDetail.value.streamingStartMinute}`.slice(-2)}`;
    compareStartTime.value = streamingStartTime.value;
    streamingEndTime.value = `${`0${streamingScheduleDetail.value.streamingEndHour}`.slice(
      -2
    )}:${`0${streamingScheduleDetail.value.streamingEndMinute}`.slice(-2)}`;
    compareEndTime.value = streamingEndTime.value;
    compareSelectedDaysOfWeek.value = streamingScheduleDetail.value.dayOfWeekList;
    selectedDaysOfWeek.value = [...compareSelectedDaysOfWeek.value];
  }

  // 表示用コンテンツリスト作成処理
  contentList.value = streamGroupDetail.value.contentDtoList.map((contentDto, index) => ({
    ...contentDto,
    isIncludeMovie: Number(
      contentDto.contentMaterialList.some(
        (contentMaterial) => contentMaterial.materialDuration !== null
      )
    ),
    contentDuration: Number(streamGroupDetail.value.contentDurationList[index]),
  }));

  isLoadingDone.value = true;
};

/**
 * ヘッダメニュ表示要否更新
 */
const setIsOpenHeaderMenu = (isOpen) => {
  isOpenHeaderMenu.value = isOpen;
  if (isOpenHeaderMenu.value) {
    disableScroll();
  } else {
    enableScroll();
  }
};

/**
 * コンテンツ選択画面遷移処理
 */
const moveContentSelect = () => {
  setIsOpenHeaderMenu(false);
  store.commit('streamGroup/setIsStreamGroupUpdate', 1);
  // 後続画面における既存データ表示のためのstreamGroupStoreへのデータ格納
  store.commit('streamGroup/setStreamGroupName', streamGroupName.value);
  store.commit('streamGroup/setIsStreaming', streamGroupDetail.value.isStreaming);
  store.commit('streamGroup/setStreamGroupContentList', contentList.value);

  router.push('/ja/streamGroup/contentSelect');
};

const deleteButtonClick = () => {
  // 配信グループ削除処理
  const DeleteStreamGroupParameters = new URLSearchParams();
  DeleteStreamGroupParameters.append('streamGroupId', streamGroupDetail.value.streamGroupId);
  DeleteStreamGroupParameters.append('userId', store.getters['userInformation/getUserId']);

  axios
    .delete(`${API_BASE_URL}/streamgroup`, {
      headers: {
        Authorization: localStorage.getItem('token'),
      },
      data: DeleteStreamGroupParameters,
      timeout: AXIOS_TIMEOUT,
    })
    .then(() => {
      router.push('/ja/streamGroup');
    })
    .catch((error) => {
      // Axios共通エラーハンドリング処理をCall
      handleAxiosCommonException(error);
    })
    .finally(() => {});
};

/**
 * 配信グループ削除確認ダイアログ表示処理
 */
const clickDeleteDialog = () => {
  // 配信グループ削除処理可否メッセージ出力
  onsConfirm(t('message.SIGNAGE004I'))
    .then(() => {
      // 確認メッセージにて「OK」押下時に配信グループ削除処理を実行
      deleteButtonClick();
    })
    .catch(() => {})
    .finally(() => {
      setIsOpenHeaderMenu(false);
    });
};

/**
 * 選択した曜日が選択済曜日リストに含まれていれば削除し、含まれていなければ追加する
 * @param {*} selectedDayOfWeek 選択した曜日
 */
const dayOfWeekButtonClick = (selectedDayOfWeek) => {
  if (selectedDaysOfWeek.value.includes(selectedDayOfWeek)) {
    selectedDaysOfWeek.value = selectedDaysOfWeek.value.filter(
      (dayOfWeek) => dayOfWeek !== selectedDayOfWeek
    );
  } else {
    selectedDaysOfWeek.value.push(selectedDayOfWeek);
  }
};

const setStreamingStartTime = ($event) => {
  streamingStartTime.value = $event.target.value;
};

const setStreamingEndTime = ($event) => {
  streamingEndTime.value = $event.target.value;
};

/**
 * 配信予約アップロード処理
 */
const streamGroupScheduleUpload = () => {
  // 入力データが意図していない中身もしくは通信処理中の場合は処理中止
  if (!isFormValid.value || isAxiosWait.value) return;

  isAxiosWait.value = true;
  // 配信予約情報登録
  putStreamGroupSchedule(
    streamGroupId.value,
    selectedDaysOfWeek.value,
    streamingStartTime.value,
    streamingEndTime.value,
    streamingScheduleId.value
  )
    .then(() => {
      // 配信グループ管理画面へ遷移
      router.push({
        name: 'ja-stream-group-wrapper',
      });
    })
    .catch(() => {})
    .finally(() => {
      isAxiosWait.value = false;
    });
};

// マウント時
onMounted(() => {
  getStreamGroupDetail();
});
</script>

<style scoped>
.ja-stream-group-detail {
  margin: auto;
  display: flex;
  justify-content: center;
  align-items: center;
}
.gray-color {
  color: #aaaaaa;
}
.header-back-button {
  background: transparent;
  border: #fff;
  font-size: 4rem;
  left: 2rem;
  position: absolute;
}
.header-delete-button {
  background: transparent;
  border: #fff;
  font-size: 4rem;
  right: 2rem;
  position: absolute;
}
.stream-group-detail-area {
  box-sizing: border-box;
  background-color: #fff;
  height: auto;
  margin: auto;
  padding-top: 10vh;
  padding-top: 10dvh;
  padding-bottom: 15vh;
  padding-bottom: 15dvh;
  z-index: 1;
  max-width: 840px;
}
.stream-group-element-area {
  margin: auto auto 2rem;
  width: 90%;
}
label {
  display: block;
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 1.5rem;
}
p {
  font-size: 1.5rem;
  margin: 0;
}
.duration-wrapper {
  height: 100%;
  width: 100%;
}
.stream-group-duration {
  padding: 0rem 0rem 0rem 1rem;
  font-size: 1.5rem;
}
.footer-button-area {
  height: 100%;
  width: 90%;
}
.footer-button {
  background: #fff;
  border: solid 1px #cccccc;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  color: #222222;
}
.header-menu-wrapper {
  border: none;
  background-color: inherit;
  right: 1rem;
  position: absolute;
}
.header-menu-list {
  position: absolute;
  top: 100%;
  right: 0;
  width: 10rem;
  box-sizing: border-box;
  background-color: white;
  border: 1px #ddd solid;
  border-radius: 0.5rem;
  z-index: 2;
}
.header-menu-list-mask {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.2);
  border: none;
  z-index: 1;
}
.menu-button {
  border: none;
  background-color: inherit;
}
.header-menu-item {
  width: 100%;
  box-sizing: border-box;
  font-weight: normal;
  font-size: 1.8rem;
  border: none;
  background-color: inherit;
  text-align: center;
  padding: 1rem 2rem;
}
/* フッター */
.footer {
  max-width: 840px;
}
.delete-button {
  color: red;
}
.footer-back-button {
  background: #fff;
  border: solid 1px #cccccc;
  color: #222222;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 35%;
  height: 5.5rem;
}
.footer-upload-button {
  box-shadow: 0rem 0.2rem #21963b;
  color: #fff;
  margin: auto;
  padding: 1rem 0;
  text-align: center;
  width: 55%;
  overflow: visible;
  height: 5.5rem;
  cursor: pointer;
}
.footer-upload-button:hover {
  opacity: 0.8;
}
.footer {
  height: 9rem;
  flex-direction: column;
}
</style>
